/**
 * The installable app's web manifest and the head it needs, per build mode (#303).
 *
 * Pure data, no DOM and no Vite: `vite.config.ts` turns it into `manifest.webmanifest` and into
 * `index.html`'s head at build time, and the tests read it directly. The build mode is the one the
 * shell already reads (`src/shell/environment.ts`): `staging` builds the staging app, anything else
 * the production one.
 *
 * STAGING MUST NEVER LOOK LIKE PRODUCTION once installed (the Director, 2026-10-05): its name says
 * staging, its icons and browser colour are orange instead of brand blue, and the app draws an
 * orange bar on every page (`StagingBar`). The orange is not a brand colour on purpose: it is
 * neither the action coral nor the attention amber, so it cannot be read as either.
 */

/** The staging orange, also the background of the icons `scripts/make-app-icons.py` makes. */
export const STAGING_COLOR = '#e8730c';
/** Text on the staging orange: near-black, far above 4.5:1. */
export const ON_STAGING_COLOR = '#2b1300';

/** The light frame's colour, which `index.html`'s bootstrap already gives the browser chrome. */
const LIGHT_FRAME = '#ecf0f3';
const DARK_FRAME = '#07121a';

export type AppFlavour = 'production' | 'staging';

export function flavourOf(mode: string): AppFlavour {
  return mode === 'staging' ? 'staging' : 'production';
}

export type WebManifest = {
  id: string;
  name: string;
  short_name: string;
  description: string;
  lang: string;
  start_url: string;
  scope: string;
  display: 'standalone';
  /** Chromium: a link into the app reuses the open window instead of opening a new one. */
  launch_handler: { client_mode: ('navigate-existing' | 'auto')[] };
  background_color: string;
  theme_color: string;
  icons: { src: string; sizes: string; type: 'image/png'; purpose: 'any' | 'maskable' }[];
};

function iconDir(flavour: AppFlavour): string {
  return flavour === 'staging' ? '/brand/staging' : '/brand';
}

export function webManifest(flavour: AppFlavour): WebManifest {
  const dir = iconDir(flavour);
  const staging = flavour === 'staging';
  return {
    id: '/',
    name: staging ? 'DGB Console (staging)' : 'DGB Console',
    // «Staging» first: Android cuts a home-screen label at about 12 characters.
    short_name: staging ? 'Staging DGB' : 'DGB Console',
    description: staging
      ? 'Ambiente di prova di DGB Console: non è la produzione.'
      : 'La console di DGB.',
    lang: 'it',
    start_url: '/',
    scope: '/',
    display: 'standalone',
    // Every console link (an email's, for instance) lands in the window that is already open;
    // `auto` is the fallback for a browser that does not know the first value.
    launch_handler: { client_mode: ['navigate-existing', 'auto'] },
    background_color: LIGHT_FRAME,
    theme_color: staging ? STAGING_COLOR : LIGHT_FRAME,
    icons: [
      { src: `${dir}/android-chrome-192.png`, sizes: '192x192', type: 'image/png', purpose: 'any' },
      { src: `${dir}/android-chrome-512.png`, sizes: '512x512', type: 'image/png', purpose: 'any' },
      { src: `${dir}/maskable-192.png`, sizes: '192x192', type: 'image/png', purpose: 'maskable' },
      { src: `${dir}/maskable-512.png`, sizes: '512x512', type: 'image/png', purpose: 'maskable' },
    ],
  };
}

/**
 * `index.html` as the flavour serves it: the manifest link and the iOS home-screen title for
 * both; for staging also the orange icons, and the orange browser chrome in place of the two
 * frame colours the theme bootstrap writes.
 */
export function headFor(html: string, flavour: AppFlavour): string {
  const manifest = webManifest(flavour);
  const tags =
    '    <link rel="manifest" href="/manifest.webmanifest" />\n' +
    '    <meta name="mobile-web-app-capable" content="yes" />\n' +
    `    <meta name="apple-mobile-web-app-title" content="${manifest.short_name}" />\n`;
  let out = html.replace('    <title>', `${tags}    <title>`);
  if (flavour === 'staging') {
    out = out
      .replaceAll('href="/brand/', 'href="/brand/staging/')
      .replaceAll(LIGHT_FRAME, STAGING_COLOR)
      .replaceAll(DARK_FRAME, STAGING_COLOR)
      .replace('<title>DGB Console</title>', '<title>DGB Console (staging)</title>')
      // The hook `shell.css` makes room for the staging tab by (phone header, see there).
      .replace('<html lang="it">', '<html lang="it" data-environment="staging">');
  }
  return out;
}

/**
 * `offline.html` as the flavour serves it. An installed staging app that goes offline must still
 * say it is staging, so its copy gets the same 4 px stripe and tab as `StagingBar`, drawn in plain
 * CSS: the page has no app, no script and no network.
 */
export function offlinePageFor(html: string, flavour: AppFlavour): string {
  if (flavour !== 'staging') {
    return html;
  }
  const bar =
    `    <div style="position:fixed;inset:0 0 auto;height:4px;background:${STAGING_COLOR}"></div>\n` +
    `    <p style="position:fixed;top:0;left:50%;transform:translateX(-50%);margin:0;` +
    `padding:2px 12px;white-space:nowrap;border-radius:0 0 6px 6px;font-size:12px;font-weight:600;` +
    `background:${STAGING_COLOR};color:${ON_STAGING_COLOR}">` +
    'Staging — ambiente di prova, non è la produzione</p>\n';
  return html
    .replace('<body>\n', `<body>\n${bar}`)
    .replace(
      '<title>DGB Console — sei offline</title>',
      '<title>DGB Console (staging) — sei offline</title>',
    );
}
